import { html, LitElement, PropertyValues, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { SmartViewCategory, SmartViewEvents, SmartViewItemConfiguration } from '../../genericGridSmartViews/types';
import { FontSizes } from '../../../types/common/types';
import _, { isEqual } from 'lodash';
import { ListItemEvents } from '../../../ui/gzListItem/types';
import { ifDefined } from 'lit/directives/if-defined.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { GzAccordionWebComponent } from '../../../ui/gzAccordion/index';
import { emphasizeString } from '../../../utils/htmlElement.utils';
import { htmlEncode } from '../../../utils/escape.utils';

export class GenericSmartViewsListWebComponent extends LitElement {
  // see https://lit-element.polymer-project.org/guide/properties

  DEFAULT_CATEGORY = 'default';
  @property({
    type: Array,
    attribute: false,
    reflect: false,
  })
  _categories: Array<any>;

  @property({ type: Boolean })
  public hasInputSearch = true;

  @property({
    type: Object,
    attribute: false,
  })
  public translations;

  @state()
  private _initialCategories;

  @state()
  private _searchText = '';

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  /**
   * default search function: case-insensitive search for the words in search term to be found in label
   */
  public searchFn: (string, SmartViewItemConfiguration) => boolean = (
    filter: string,
    item: SmartViewItemConfiguration
  ): boolean => {
    if (filter === '') {
      return true;
    }

    filter = filter.toLowerCase();
    const lowercaseLabel = item.label.trim().toLowerCase();
    const filterWords = filter.split(/\s+/);
    for (const filterWord of filterWords) {
      if (!lowercaseLabel.includes(filterWord)) {
        return false;
      }
    }

    return true;
  };

  render() {
    return html`
      <div class="content">
        ${this.hasInputSearch
          ? html`
              <div class="input-search-container">
                <gz-input-search
                  @change="${this._handleInputChange}"
                  placeholder="${this.translations.searchPlaceholderString}"
                  id="views-input-search"
                />
              </div>
            `
          : nothing}
        ${this.renderNoItemsFound()}
        <div class="panel-content">
          ${this._initialCategories.map((category: SmartViewCategory) =>
            this._searchText.length !== 0 && !category.items.length
              ? nothing
              : html` <gz-accordion
                  isOpen=${ifDefined(category.isOpen)}
                  forceIsOpen=${ifDefined(category.isOpen)}
                  @open="${this.handleOpen}"
                  @close="${this.handleClose}"
                  isCompact="true"
                  align="left"
                  data-category="${category.sectionTitle.toUpperCase()}"
                >
                  <span slot="dynamic-title-content" class="accordion-slot d-inline-flex">
                    <gz-tooltip class="accordion-title" tooltiptext="${category.sectionTitle}" isellipsis="true"
                      >${category.sectionTitle}</gz-tooltip
                    >
                    <gz-text class="accordion-count" fontWeight="300" fontSize="10">(${category.items.length})</gz-text>
                  </span>
                  ${this.renderAccordionItems(category)}
                </gz-accordion>`
          )}
        </div>
      </div>
    `;
  }

  //force the setting of isOpen on child component on update (without this the render function for the child accordion and the setting of isOpen would not be set)
  updated(changedProperties: PropertyValues): void {
    if (changedProperties.get('_searchText') === undefined) {
      return;
    }
    this.shadowRoot?.querySelectorAll('gz-accordion').forEach((elemNode: GzAccordionWebComponent): void => {
      if (elemNode.getAttribute('forceIsOpen')) {
        elemNode.isOpen = true;
      }
    });
  }

  renderNoItemsFound() {
    let haveItems = false;

    for (const category of this._initialCategories) {
      if (category.items.length) {
        haveItems = true;

        break;
      }
    }

    return haveItems ? nothing : html`<div class="no-items">${this.translations.emptySavedViewsText}</div>`;
  }

  renderAccordionItems(category) {
    if (category.items.length) {
      return html` ${category.items.map(
        view =>
          html` <gz-list-item
            .inlineActions="${category.inlineActions}"
            .itemData="${view}"
            .selectDisabled="${view.selectDisabled}"
            .editable=${view.editable}
            .active="${view.active}"
            .errorMessage="${category.smartViewValidators}"
            @rename="${this.handleRename}"
            @save="${this.handleSave}"
            @cancel="${this.handleCancel}"
            @itemClick="${this.handleItemClick}"
            isCompact="true"
            noSpaceEscape=${ifDefined(category.noSpaceEscape) ? category.noSpaceEscape : undefined}
            inputMaxLength=${ifDefined(category.smartViewMaxInputLength) ? category.smartViewMaxInputLength : undefined}
            ><span class="accordion-item"
              >${!view.decoratedTextOnSearch ? view.label : nothing}${unsafeHTML(
                view.decoratedTextOnSearch ? view.decoratedTextOnSearch : ''
              )}</span
            ></gz-list-item
          >`
      )}`;
    } else {
      return html` <gz-list-item emptyState="true" isCompact="true" fontSize="${FontSizes.PARAGRAPH_12}">
        <span class="empty-text">${category.emptyStateItemText}</span>
      </gz-list-item>`;
    }
  }

  set categories(prop: Array<any>) {
    const oldCategories = this._categories;
    this._categories = prop;
    this._initialCategories = _.cloneDeep(this._categories);
    if (this._searchText.length !== 0) {
      this.filterCategories(this._searchText);
    }
    this.requestUpdate('categories', oldCategories);
  }

  @property({
    type: Array,
    hasChanged: function (newVal: any, oldVal: any) {
      return !isEqual(newVal, oldVal);
    },
  })
  get categories() {
    return this._categories;
  }

  public updateActiveAndSelectItem(viewId: string) {
    let selectedView = null;
    this._initialCategories.forEach(category => {
      category.items.forEach(view => {
        if (view._id === viewId) {
          selectedView = view;
        }
      });
    });

    if (!selectedView) {
      return;
    }
    this.triggerSelectedEvent(selectedView);
  }

  public getViewById(viewId: string): SmartViewItemConfiguration | null {
    let selectedView = null;
    this._initialCategories.forEach(category => {
      category.items.forEach(view => {
        if (view._id === viewId) {
          selectedView = view;
        }
      });
    });

    return selectedView;
  }

  public getDefaultViewByOriginalLabel(viewLabel: string): SmartViewItemConfiguration | null {
    let selectedView = null;
    this._initialCategories.forEach(category => {
      category.items.forEach(view => {
        if (view.originalLabel === viewLabel && category.type === this.DEFAULT_CATEGORY) {
          selectedView = view;
        }
      });
    });

    return selectedView;
  }

  public getViewByLabel(viewLabel: string): SmartViewItemConfiguration | null {
    let selectedView = null;
    this._initialCategories.forEach(category => {
      category.items.forEach(view => {
        if (view.label === viewLabel) {
          selectedView = view;
        }
      });
    });

    return selectedView;
  }

  public focusOnActiveItem(): void {
    this.shadowRoot?.querySelector('[active]')?.scrollIntoView(false);
  }

  private handleItemClick(event) {
    this.updateActiveAndSelectItem(event.detail.itemData._id);
  }

  public connectedCallback() {
    super.connectedCallback();
  }

  public disconnectedCallback() {
    super.disconnectedCallback();
  }

  _handleInputChange(event) {
    const searchInput = event.detail.sourceEvent.trim().toLowerCase();
    this._searchText = searchInput;
    this.filterCategories(searchInput);
  }

  filterCategories(filter: string): void {
    const filteredCategories = _.cloneDeep(this.categories);
    filteredCategories.forEach((category: SmartViewCategory): void => {
      category.items = category.items.filter(item => this.searchFn(filter, item));
      if (category.items.length && filter !== '') {
        category.isOpen = true;
      }
      category.items = category.items.map(item => {
        return {
          ...item,
          decoratedTextOnSearch: emphasizeString(filter, htmlEncode(item.label)),
        };
      });
    });

    this._initialCategories = filteredCategories;
  }

  shouldUpdate(changedProperties) {
    return this.translations && changedProperties.has('_initialCategories');
  }

  constructor() {
    super();
  }

  /**
   * dk why i have to handle event and dispatch again
   * @param event
   */
  handleSave(event) {
    const eventSave = new CustomEvent(ListItemEvents.SAVE, {
      detail: {
        ...event.detail,
      },
      bubbles: true,
    });
    this.dispatchEvent(eventSave);
  }

  handleCancel() {
    this.setListItemsSelectDisabled();
  }

  private handleOpen(event) {
    this.handleOpenClose(event, true);
  }

  private handleClose(event) {
    this.handleOpenClose(event, false);
  }

  private handleOpenClose(event, opened: boolean) {
    const evCategory = event.currentTarget.getAttribute('data-category');
    this.dispatchEvent(
      new CustomEvent(SmartViewEvents.SMART_VIEW_CATEGORY_OPEN, {
        detail: {
          sectionTitle: evCategory,
          opened: opened,
        },
        bubbles: true,
      })
    );
  }

  handleRename(event) {
    const listItem = event.currentTarget;
    listItem.editable = true;
    this.resetListItemsToDefaultOnRename(listItem);
    listItem.selectDisabled = false;
  }

  resetListItemsToDefaultOnRename(listItem) {
    const categories = _.cloneDeep(this._initialCategories);
    categories.forEach(category => {
      category.items.forEach(view => {
        view.selectDisabled = view._id !== listItem.itemData._id;
      });
    });
    this._initialCategories = categories;
  }

  private setListItemsSelectDisabled() {
    const categories = _.cloneDeep(this._initialCategories);
    categories.forEach(category => {
      category.items.forEach(view => {
        view.selectDisabled = false;
      });
    });
    this._initialCategories = categories;
  }

  private updateActiveItem(activeView) {
    const categories = _.cloneDeep(this._initialCategories);
    categories.forEach(category => {
      category.items.forEach(view => {
        view.selectDisabled = false;
        view.editable = false;
        view.active = activeView._id === view._id;
        if (activeView._id === view._id) {
          activeView = view;
        }
      });
    });
    this._initialCategories = categories;
    return activeView;
  }

  private triggerSelectedEvent(view) {
    this.dispatchEvent(
      new CustomEvent(SmartViewEvents.SMART_VIEW_SELECTED, {
        detail: {
          smartViewConfiguration: view,
        },
        bubbles: true,
      })
    );
  }

  firstUpdated(changedProperties) {
    super.firstUpdated(changedProperties);
  }
}
